import { ArrowLeft, Check, CheckCircle2, Circle } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Markdown } from '@/components/ui/markdown';
import { Skeleton } from '@/components/ui/skeleton';
import { reviewStatusLabel, reviewStatusVariant } from '@/features/hrm/reviews/constants';
import type { ReviewActionItem } from '@/features/hrm/reviews';
import {
  useAcknowledgeCabinetReview,
  useCabinetReview,
  useSetCabinetReviewActionItemDone,
} from '../queries';

const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleDateString() : '—');

/** My Space → Performance Reviews → read one. Drafts 404 server-side. The
 * employee can acknowledge a submitted review and tick their action items
 * done/undone so reviewer and employee can walk the list together in the 1:1. */
export function CabinetReviewDetailPage() {
  const { publicId = '' } = useParams();
  const navigate = useNavigate();
  const { data: review, isPending, isError } = useCabinetReview(publicId);
  const acknowledge = useAcknowledgeCabinetReview();
  const toggleItem = useSetCabinetReviewActionItemDone();

  const back = () => navigate('/me/reviews');

  if (isPending) {
    return (
      <div className="max-w-3xl space-y-4">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (isError || !review) {
    return (
      <div className="max-w-3xl space-y-4">
        <Button variant="ghost" size="sm" className="-ml-2" onClick={back}>
          <ArrowLeft /> Performance reviews
        </Button>
        <p className="text-muted-foreground">This review isn’t available.</p>
      </div>
    );
  }

  const canAcknowledge = review.status === 'submitted';

  return (
    <div className="max-w-3xl space-y-6">
      <Button variant="ghost" size="sm" className="text-muted-foreground -ml-2" onClick={back}>
        <ArrowLeft /> Performance reviews
      </Button>

      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">{review.period}</h1>
        <Badge variant={reviewStatusVariant(review.status)}>
          {reviewStatusLabel(review.status)}
        </Badge>
        {canAcknowledge ? (
          <Button
            className="ml-auto"
            onClick={() => acknowledge.mutate(review.public_id)}
            disabled={acknowledge.isPending}
          >
            <CheckCircle2 /> Acknowledge
          </Button>
        ) : null}
      </div>

      {canAcknowledge ? (
        <p className="text-muted-foreground text-sm">
          Review the summary and action items with your reviewer, then acknowledge to confirm
          you’ve read and discussed it.
        </p>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Details</CardTitle>
        </CardHeader>
        <CardContent className="text-muted-foreground grid gap-4 text-sm sm:grid-cols-3">
          <Field label="Period">{review.period}</Field>
          <Field label="Reviewed">{fmtDate(review.review_date)}</Field>
          <Field label="Rating">{review.rating ?? '—'}</Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Summary</CardTitle>
        </CardHeader>
        <CardContent>
          {review.summary ? (
            <Markdown>{review.summary}</Markdown>
          ) : (
            <span className="text-muted-foreground text-sm">—</span>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Action items</CardTitle>
        </CardHeader>
        <CardContent>
          {review.action_items.length === 0 ? (
            <p className="text-muted-foreground text-sm">No action items.</p>
          ) : (
            <ul className="space-y-1">
              {review.action_items.map((item) => (
                <ActionItemRow
                  key={item.id}
                  item={item}
                  pending={toggleItem.isPending && toggleItem.variables?.id === item.id}
                  onToggle={() => toggleItem.mutate({ id: item.id, isDone: !item.is_done })}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ActionItemRow({
  item,
  pending,
  onToggle,
}: {
  item: ReviewActionItem;
  pending: boolean;
  onToggle: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        disabled={pending}
        aria-pressed={item.is_done}
        className="hover:bg-accent/30 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm disabled:opacity-60"
      >
        {item.is_done ? (
          <Check className="size-4 shrink-0 text-green-600" />
        ) : (
          <Circle className="text-muted-foreground size-4 shrink-0" />
        )}
        <span className={item.is_done ? 'text-muted-foreground line-through' : ''}>
          {item.title}
        </span>
        {item.due_date ? (
          <span className="text-muted-foreground ml-auto text-xs">due {fmtDate(item.due_date)}</span>
        ) : null}
      </button>
    </li>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase">{label}</p>
      <div className="text-foreground mt-1">{children}</div>
    </div>
  );
}
